import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";

import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Jadwal from "@/pages/Jadwal";
import UploadAlokasi from "@/pages/UploadAlokasi";
import Teknisi from "@/pages/Teknisi";
import Korwil from "@/pages/Korwil";
import Sekolah from "@/pages/Sekolah";
import Unit from "@/pages/Unit";
import Attention from "@/pages/Attention";
import Performance from "@/pages/Performance";
import LMS from "@/pages/LMS";
import Broadcast from "@/pages/Broadcast";
import Report from "@/pages/Report";
import Users from "@/pages/Users";
import Finance from "@/pages/Finance";


/* ============================================================
   ROLE ACCESS
   ============================================================ */

// Finance dapat diakses oleh role berikut.
// Pembatasan MENU dilakukan di Finance.jsx / Layout.jsx.
const FINANCE_ROLES = [
  "SUPER_ADMIN",
  "KORWIL",
  "KORDA",
  "KOPROV",
  "ADMIN_KORWIL",
];
const UPLOADER_ROLES = ["SUPER_ADMIN", "KORWIL", "ADMIN_KORWIL"];


/* ============================================================
   GUARD
   ============================================================ */

function Guard({
  children,
  adminOnly = false,
  uploaderOnly = false,
  financeOnly = false,
}) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0f17]">
        <Loader2 className="h-7 w-7 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  /* ----------------------------------------------------------
     SUPER ADMIN ONLY
     ---------------------------------------------------------- */

  if (adminOnly && user.role !== "SUPER_ADMIN") {
    return <Navigate to="/" replace />;
  }

  /* ----------------------------------------------------------
     UPLOAD ALOKASI
     Hanya SUPER_ADMIN dan KORWIL
     ---------------------------------------------------------- */

  if (uploaderOnly && !UPLOADER_ROLES.includes(user.role)) {
    return <Navigate to="/jadwal" replace />;
  }

  /* ----------------------------------------------------------
     FINANCE
     
     SUPER_ADMIN  -> Finance penuh
     KORWIL       -> Finance penuh
     KORDA        -> Finance KORDA saja
     ADMIN_KORWIL -> Finance, Setoran BAPP Pusat saja
     
     Pembatasan menu dilakukan di Finance.jsx.
     Guard hanya memastikan role tersebut memang
     memiliki akses ke halaman Finance.
     ---------------------------------------------------------- */

  if (financeOnly && !FINANCE_ROLES.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
}


/* ============================================================
   APPLICATION SHELL
   ============================================================ */

function Shell() {
  const { user, loading } = useAuth();

  return (
    <Routes>

      {/* ======================================================
          LOGIN
          ====================================================== */}

      <Route
        path="/login"
        element={
          loading
            ? null
            : user
              ? <Navigate to="/" replace />
              : <Login />
        }
      />


      {/* ======================================================
          COMMAND CENTER
          TIDAK DIUBAH
          ====================================================== */}

      <Route
        path="/"
        element={
          <Guard>
            <Dashboard />
          </Guard>
        }
      />


      {/* ======================================================
          JADWAL
          ====================================================== */}

      <Route
        path="/jadwal"
        element={
          <Guard>
            <Jadwal />
          </Guard>
        }
      />


      {/* ======================================================
          UPLOAD ALOKASI
          ====================================================== */}

      <Route
        path="/jadwal/upload"
        element={
          <Guard uploaderOnly>
            <UploadAlokasi />
          </Guard>
        }
      />


      {/* ======================================================
          DATABASE TEKNISI
          ====================================================== */}

      <Route
        path="/teknisi"
        element={
          <Guard>
            <Teknisi />
          </Guard>
        }
      />


      {/* ======================================================
          KORWIL MONITORING
          ====================================================== */}

      <Route
        path="/korwil"
        element={
          <Guard>
            <Korwil />
          </Guard>
        }
      />


      {/* ======================================================
          DATABASE SEKOLAH
          ====================================================== */}

      <Route
        path="/sekolah"
        element={
          <Guard>
            <Sekolah />
          </Guard>
        }
      />


      {/* ======================================================
          UNIT INSTALASI
          ====================================================== */}

      <Route
        path="/unit"
        element={
          <Guard>
            <Unit />
          </Guard>
        }
      />


      {/* ======================================================
          PERLU TINDAKAN
          ====================================================== */}

      <Route
        path="/attention"
        element={
          <Guard>
            <Attention />
          </Guard>
        }
      />


      {/* ======================================================
          PERFORMANCE
          ====================================================== */}

      <Route
        path="/performance"
        element={
          <Guard>
            <Performance />
          </Guard>
        }
      />

      {/* ======================================================
          LMS
          Semua user login dapat membaca/download.
          Upload/hapus diamankan di backend untuk SUPER_ADMIN.
          ====================================================== */}

      <Route
        path="/lms"
        element={
          <Guard>
            <LMS />
          </Guard>
        }
      />

      {/* ======================================================
          BROADCAST / PESAN
          ====================================================== */}

      <Route
        path="/broadcast"
        element={
          <Guard>
            <Broadcast />
          </Guard>
        }
      />


      {/* ======================================================
          REPORT
          ====================================================== */}

      <Route
        path="/report"
        element={
          <Guard>
            <Report />
          </Guard>
        }
      />


      {/* ======================================================
          USER MANAGEMENT
          Hanya SUPER_ADMIN
          ====================================================== */}

      <Route
        path="/users"
        element={
          <Guard adminOnly>
            <Users />
          </Guard>
        }
      />


      {/* ======================================================
          FINANCE

          BOLEH:
          - SUPER_ADMIN -> Finance penuh
          - KORWIL -> Finance penuh
          - KORDA -> Finance KORDA
          - ADMIN_KORWIL -> Setoran BAPP Pusat saja
          ====================================================== */}

      <Route
        path="/finance"
        element={
          <Guard financeOnly>
            <Finance />
          </Guard>
        }
      />


      {/* ======================================================
          FALLBACK
          ====================================================== */}

      <Route
        path="*"
        element={<Navigate to="/" replace />}
      />

    </Routes>
  );
}


/* ============================================================
   APP
   ============================================================ */

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <Shell />
        </ThemeProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
